import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { THERAPY_STATUS_CONFIG, THERAPY_PAYMENT_CONFIG } from "@/lib/status-configs";
import { getSessionDisplayStatus } from "@/lib/session-display-status";
import { toast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { CheckCircle, CircleNotch, Bank } from "@phosphor-icons/react";
import type { Therapy } from "@/types";

const STATUS_CONFIG = THERAPY_STATUS_CONFIG;
const PAYMENT_CONFIG = THERAPY_PAYMENT_CONFIG;

const dotTextBase = "inline-flex items-center gap-1 text-[11px] font-medium whitespace-nowrap";

export function StatusBadge({
  status,
  scheduledAt,
}: {
  status: Therapy["status"] | "pending_outcome";
  scheduledAt?: string;
}) {
  const displayStatus = getSessionDisplayStatus({ status, scheduledAt });
  const cfg = STATUS_CONFIG[displayStatus as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.scheduled;
  return (
    <span className={dotTextBase}>
      <span className={cn("size-1.5 shrink-0 rounded-full", cfg.dot)} />
      <span className={cfg.color.replace(/bg-\S+\/\d+\s?/, "")}>{cfg.label}</span>
    </span>
  );
}

export function PaymentBadge({ status }: { status: Therapy["paymentStatus"] }) {
  const cfg = PAYMENT_CONFIG[status];
  return (
    <span className={dotTextBase}>
      <span className={cn("size-1.5 shrink-0 rounded-full", cfg.dot)} />
      <span className={cfg.color.replace(/bg-\S+\/\d+\s?/, "")}>{cfg.label}</span>
    </span>
  );
}

export function ConfirmPaymentButton({ sessionId }: { sessionId: string }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  async function handleConfirm() {
    setLoading(true);
    try {
      const res = await fetch(`/api/sessions/${sessionId}/confirm-payment`, {
        method: "POST",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || "Greška");
      }
      toast.success("Uplata potvrđena");
      queryClient.invalidateQueries({ queryKey: ["admin", "therapies"] });
      setOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Greška pri potvrdi uplate");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="group inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full bg-emerald-600 px-3.5 text-[11px] font-semibold text-white shadow-sm shadow-emerald-600/25 transition-all hover:shadow-md hover:shadow-emerald-600/35 hover:brightness-110 active:scale-[0.97]"
      >
        <CheckCircle className="size-3.5 transition-transform group-hover:scale-110" weight="fill" />
        Potvrdi uplatu
      </button>

      {open && (
        <Dialog open onClose={() => !loading && setOpen(false)} className="sm:max-w-sm">
          <DialogHeader onClose={loading ? undefined : () => setOpen(false)}>
            <AdminDialogHeading
              icon={Bank}
              title="Potvrdi uplatu"
              description="Bankovni transfer za ovu sesiju."
              tone="success"
            />
          </DialogHeader>

          <DialogBody className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              Sesija će biti označena kao plaćena i ući će u obračun isplate terapeuta. Ovu radnju nije moguće poništiti.
            </p>
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={loading} className="bg-transparent">
              Nazad
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={loading}
              className="bg-emerald-600 text-white hover:bg-emerald-600/90"
            >
              {loading ? <CircleNotch className="size-4 animate-spin" /> : <CheckCircle className="size-4" weight="fill" />}
              Potvrdi uplatu
            </Button>
          </DialogFooter>
        </Dialog>
      )}
    </>
  );
}

export function PayoutBadge({ payoutId }: { payoutId: string | null }) {
  const isPaid = payoutId !== null;
  return (
    <span className={dotTextBase}>
      <span className={cn("size-1.5 shrink-0 rounded-full", isPaid ? "bg-emerald-500" : "bg-amber-500")} />
      <span className={isPaid ? "text-emerald-600" : "text-amber-600"}>
        {isPaid ? "Isplaćeno" : "Čeka"}
      </span>
    </span>
  );
}
